import "css/import.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";

import { useAppStore } from "lib/store/useAppStore";

import Spinner from "components/Spinner.jsx";
import LoginContainer from "components/login/LoginContainer.jsx";
import LoginPage from "pages/auth/LoginPage.jsx";

import Container from "components/Container.jsx";
import ProjectOverviewPage from "pages/projects/ProjectOverviewPage.jsx";
import NewProjectPage from "pages/projects/NewProjectPage";
import ProjectSettingsPage from "pages/projects/ProjectSettingsPage";
import ProjectReportPage from "pages/projects/ProjectReportPage";
import ProjectDetailPage from "pages/projects/detail/ProjectDetailPage.jsx";
import ProjectFileUploadPage from "pages/projects/detail/ProjectFileUploadPage.jsx";
import ImageFilePage from "pages/files/image/ImageFilePage.jsx";
import VideoFilePage from "pages/files/video/VideoFilePage";
import FacilityQrPage from "pages/qr/FacilityQrPage";
import { getPostLoginPath } from "lib/utils/authNavigation";

import UserSettingsPage from "pages/settings/UserSettingsPage";
import OrganizationPage from "pages/organizations/OrganizationPage";
import GlobalAlert from "components/GlobalAlert";
import GlobalNotificationCenter from "components/GlobalNotificationCenter";

import AdminHomePage from "pages/admin/AdminHomePage";
import AdminUsersPage from "pages/admin/AdminUsersPage";
import AdminContentPage from "pages/admin/AdminContentPage";
import AdminLogsPage from "pages/admin/AdminLogsPage";
import AdminFilesPage from "pages/admin/AdminFilesPage";
import AdminImageFilePage from "pages/admin/AdminImageFilePage";
import AdminVideoFilePage from "pages/admin/AdminVideoFilePage";
import AdminProjectsPage from "pages/admin/AdminProjectsPage";
import AdminKpiDetailPage from "pages/admin/AdminKpiDetailPage";
import AdminOperatorHomePage from "pages/admin/AdminOperatorHomePage";
import AdminOperatorPermissionsPage from "pages/admin/AdminOperatorPermissionsPage";
import AdminSignupRequestsPage from "pages/admin/AdminSignupRequestsPage";

import NoticePage from "pages/notices/NoticePage.jsx";
import SampleThreeDPage from "pages/sample3d/SampleThreeDPage";

function isRoleAdminPageRole(role) {
  return role === "admin" || role === "manager";
}

function isAdminAccessRole(role) {
  return role === "master" || isRoleAdminPageRole(role);
}

function AppRootInitializer({ children }) {
  const bootstrap = useAppStore((s) => s.bootstrap);
  const status = useAppStore((s) => s.status);
  const bootstrapping = useAppStore((s) => s.bootstrapping);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  if (status === "unknown" || bootstrapping) {
    return <Spinner />;
  }

  return children;
}

function ProtectedRoute({ children }) {
  const status = useAppStore((s) => s.status);
  const location = useLocation();
  if (status === "authenticated") return children;
  return <Navigate to="/" state={{ from: `${location.pathname}${location.search}${location.hash}` }} replace />;
}

function AdminAccessRoute({ children }) {
  const status = useAppStore((s) => s.status);
  const role = useAppStore((s) => s.user?.role);

  if (status !== "authenticated") {
    return <Navigate to="/" replace />;
  }

  if (!isAdminAccessRole(role)) {
    return <Navigate to="/project" replace />;
  }

  return children;
}

function MasterOnlyRoute({ children }) {
  const status = useAppStore((s) => s.status);
  const role = useAppStore((s) => s.user?.role);

  if (status !== "authenticated") {
    return <Navigate to="/" replace />;
  }

  if (role !== "master") {
    return (
      <Navigate to={isRoleAdminPageRole(role) ? "/admin" : "/project"} replace />
    );
  }

  return children;
}

function RoleAdminOnlyRoute({ children }) {
  const status = useAppStore((s) => s.status);
  const role = useAppStore((s) => s.user?.role);

  if (status !== "authenticated") {
    return <Navigate to="/" replace />;
  }

  if (!isRoleAdminPageRole(role)) {
    return <Navigate to={role === "master" ? "/admin" : "/project"} replace />;
  }

  return children;
}

function AdminIndexPage() {
  const role = useAppStore((s) => s.user?.role);
  return isRoleAdminPageRole(role) ? (
    <AdminOperatorHomePage />
  ) : (
    <AdminHomePage />
  );
}

function PublicOnlyRoute({ children }) {
  const status = useAppStore((s) => s.status);
  const lastSelectedProject = useAppStore((s) => s.user?.last_selected_project);
  const { state } = useLocation();
  if (status === "authenticated") return <Navigate to={getPostLoginPath(state?.from, lastSelectedProject)} replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRootInitializer>
        <GlobalAlert />
        <GlobalNotificationCenter />
        <Routes>
          <Route path="/qr/facility" element={<FacilityQrPage />} />
          <Route path="/q/:qrId" element={<FacilityQrPage />} />
          <Route
            path="/"
            element={
              <PublicOnlyRoute>
                <LoginContainer />
              </PublicOnlyRoute>
            }
          >
            <Route index element={<LoginPage />} />
          </Route>

          <Route
            path="/project"
            element={
              <ProtectedRoute>
                <Container />
              </ProtectedRoute>
            }
          >
            <Route index element={<ProjectOverviewPage />} />
            <Route path="create" element={<NewProjectPage />} />
            <Route path=":project_id/settings" element={<ProjectSettingsPage />} />
            <Route path=":project_id/report" element={<ProjectReportPage />} />
            <Route path=":project_id?" element={<ProjectDetailPage />} />
            <Route path="file_upload/:project_id" element={<ProjectFileUploadPage />} />
            <Route
              path=":project_id/file_image/:upload_id"
              element={<ImageFilePage />}
            />
            <Route
              path=":project_id/file_video/:upload_id"
              element={<VideoFilePage />}
            />
          </Route>

          <Route path="/notice" element={<Container />}>
            <Route index element={<NoticePage />} />
          </Route>

          <Route path="/sample3d" element={<SampleThreeDPage />} />

          <Route
            path="/organization"
            element={
              <ProtectedRoute>
                <Container />
              </ProtectedRoute>
            }
          >
            <Route index element={<OrganizationPage />} />
          </Route>

          <Route
            path="/user"
            element={
              <ProtectedRoute>
                <Container />
              </ProtectedRoute>
            }
          >
            <Route index element={<UserSettingsPage />} />
          </Route>

          <Route
            path="/admin"
            element={
              <AdminAccessRoute>
                <Container />
              </AdminAccessRoute>
            }
          >
            <Route index element={<AdminIndexPage />} />
            <Route
              path="users"
              element={
                <MasterOnlyRoute>
                  <AdminUsersPage />
                </MasterOnlyRoute>
              }
            />
            <Route
              path="content"
              element={
                <MasterOnlyRoute>
                  <AdminContentPage />
                </MasterOnlyRoute>
              }
            />
            <Route
              path="notices"
              element={
                <MasterOnlyRoute>
                  <Navigate to="/admin/content?tab=notice" replace />
                </MasterOnlyRoute>
              }
            />
            <Route
              path="logs"
              element={
                <MasterOnlyRoute>
                  <AdminLogsPage />
                </MasterOnlyRoute>
              }
            />
            <Route
              path="files"
              element={
                <MasterOnlyRoute>
                  <AdminFilesPage />
                </MasterOnlyRoute>
              }
            />
            <Route
              path="files/image/:fileId"
              element={
                <MasterOnlyRoute>
                  <AdminImageFilePage />
                </MasterOnlyRoute>
              }
            />
            <Route
              path="files/video/:fileId"
              element={
                <MasterOnlyRoute>
                  <AdminVideoFilePage />
                </MasterOnlyRoute>
              }
            />
            <Route
              path="projects"
              element={
                <MasterOnlyRoute>
                  <AdminProjectsPage />
                </MasterOnlyRoute>
              }
            />
            <Route
              path="kpi/:kpiId"
              element={
                <MasterOnlyRoute>
                  <AdminKpiDetailPage />
                </MasterOnlyRoute>
              }
            />
            <Route
              path="permissions"
              element={
                <RoleAdminOnlyRoute>
                  <AdminOperatorPermissionsPage />
                </RoleAdminOnlyRoute>
              }
            />
            <Route
              path="signup-requests"
              element={<AdminSignupRequestsPage />}
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppRootInitializer>
    </BrowserRouter>
  );
}
